import type { ReactNode } from 'react';
import { usePermissions } from '../hooks/usePermissions';

interface PermissionGuardProps {
  /**
   * Permiso requerido (código del permiso)
   */
  permission?: string;

  /**
   * Lista de permisos - el usuario debe tener al menos uno
   */
  anyPermission?: string[];

  /**
   * Lista de permisos - el usuario debe tener todos
   */
  allPermissions?: string[];

  /**
   * Rol requerido
   */
  role?: string;

  /**
   * Contenido a renderizar si el usuario tiene los permisos
   */
  children: ReactNode;

  /**
   * Contenido alternativo si el usuario no tiene los permisos
   */
  fallback?: ReactNode;
}

/**
 * Componente para mostrar contenido condicionalmente basado en permisos
 *
 * @example
 * // Mostrar botón solo si tiene permiso
 * <PermissionGuard permission="GUIAS_MATERIA_PRIMA_CREAR">
 *   <Button>Crear Guía</Button>
 * </PermissionGuard>
 *
 * @example
 * // Mostrar si tiene cualquiera de los permisos
 * <PermissionGuard anyPermission={['GUIAS_VER', 'GUIAS_CREAR']}>
 *   <GuiasPage />
 * </PermissionGuard>
 *
 * @example
 * // Mostrar si tiene un rol específico
 * <PermissionGuard role="Administrador">
 *   <AdminPanel />
 * </PermissionGuard>
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  anyPermission,
  allPermissions,
  role,
  children,
  fallback = null
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasRole, isSuperAdmin } = usePermissions();

  // SuperAdmin tiene acceso a todo
  if (isSuperAdmin()) {
    return <>{children}</>;
  }

  // Verificar permiso único
  if (permission && !hasPermission(permission)) {
    return <>{fallback}</>;
  }

  // Verificar al menos un permiso
  if (anyPermission && !hasAnyPermission(anyPermission)) {
    return <>{fallback}</>;
  }

  // Verificar todos los permisos
  if (allPermissions && !hasAllPermissions(allPermissions)) {
    return <>{fallback}</>;
  }

  // Verificar rol
  if (role && !hasRole(role)) {
    return <>{fallback}</>;
  }

  // Usuario tiene los permisos necesarios
  return <>{children}</>;
};

export default PermissionGuard;
